import React from 'react';
import { TrendingUp, TrendingDown, Minus, ZoomIn, Lock, Pin } from 'lucide-react';
import { Dial, STATUS_COLOURS } from './Dial';

export const fmtValue = (t) => {
  if (t.value == null) return '—';
  if (t.kind === 'money') return `$${Number(t.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  return `${t.value}${t.unit && t.kind !== 'gauge' ? t.unit : ''}`;
};

function Trend({ tile }) {
  if (tile.previous == null || tile.value == null) return <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--olive)' }} data-testid={`ops-trend-${tile.key}`}><Minus size={12} /> no change data</span>;
  const diff = Number(tile.value) - Number(tile.previous);
  if (Math.abs(diff) < 0.005) return <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--olive)' }} data-testid={`ops-trend-${tile.key}`}><Minus size={12} /> steady vs last week</span>;
  const higherGood = tile.thresholds?.higher_better || ['members_financial', 'payments_7d', 'sales_7d', 'inbox_7d', 'event_regs_7d', 'visitors_7d', 'meetings_30d', 'events_upcoming', 'sms_7d'].includes(tile.key);
  const good = higherGood ? diff > 0 : diff < 0;
  const txt = tile.kind === 'money' ? `$${Math.abs(diff).toFixed(0)}` : Math.abs(diff) % 1 ? Math.abs(diff).toFixed(1) : Math.abs(diff);
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: good ? STATUS_COLOURS.green : STATUS_COLOURS.red }} data-testid={`ops-trend-${tile.key}`}>
      {diff > 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {diff > 0 ? '+' : '−'}{txt} vs last week
    </span>
  );
}

export function MetricTile({ tile, onZoom, pinned, onPin }) {
  const colour = STATUS_COLOURS[tile.status] || STATUS_COLOURS.neutral;
  return (
    <div className="relative group">
    {onPin && (
      <button type="button" onClick={(e) => { e.stopPropagation(); onPin(tile.key); }} title={pinned ? 'Unpin' : 'Pin to the top of my dashboard'}
        className="absolute top-2 right-7 z-10 p-1 rounded transition-opacity" style={{ color: pinned ? 'var(--maroon)' : 'var(--olive)', opacity: pinned ? 1 : 0.35 }} data-testid={`ops-pin-${tile.key}`} aria-pressed={pinned}>
        {pinned ? <Pin size={13} fill="currentColor" /> : <Pin size={13} />}
      </button>
    )}
    <button type="button" onClick={() => onZoom(tile)}
      className="w-full h-full text-left bg-white rounded-xl border p-4 flex flex-col gap-2 transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2"
      style={{ borderColor: 'rgba(0,0,0,0.08)', boxShadow: `inset 4px 0 0 ${colour}` }} data-testid={`ops-tile-${tile.key}`} title="Click to zoom in">
      <div className="flex items-start justify-between gap-2 pr-5">
        <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--olive)' }}>{tile.label}</span>
        <span className="inline-flex items-center gap-1">
          {!tile.can_open && <Lock size={12} style={{ color: 'var(--olive)' }} title="Your role cannot open this area" />}
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: colour, boxShadow: tile.status === 'red' ? `0 0 0 4px ${colour}33` : 'none' }} data-testid={`ops-status-${tile.key}`} />
        </span>
      </div>
      {tile.kind === 'gauge' ? (
        <div className="flex justify-center"><Dial value={tile.value} status={tile.status} size={130} unit={tile.unit} label={tile.label} testid={`ops-dial-${tile.key}`} /></div>
      ) : (
        <div className="font-title leading-none" style={{ color: 'var(--navy)', fontSize: '2.4rem' }} data-testid={`ops-value-${tile.key}`}>{fmtValue(tile)}</div>
      )}
      <Trend tile={tile} />
      <ZoomIn size={14} className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-70 transition-opacity" style={{ color: 'var(--navy)' }} />
    </button>
    </div>
  );
}
